import { alpha, Box, ButtonBase, Collapse, SxProps, Theme } from '@mui/material';
import { ChevronRight, Split } from 'lucide-react';
import { useState } from 'react';
import { SettingsSectionRow } from 'src/components/SettingsSection';
import { useConversationAssigneeGroupsOptions } from 'src/graphql/entities/conversation-assignee-group';
import { useSlaRoutingPolicy } from 'src/pages/Settings/preferences/hooks/use-sla-routing-policy';
import { SlaRoutingConfig } from './sla-routing-config';
import { SlaRoutingSaveBar } from './sla-routing-save-bar';

const disclosureChevronSx: SxProps<Theme> = {
  display: 'inline-flex',
  color: 'custom.OV.sys.onSurfaceVariant',
  transition: (theme) =>
    theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  '& svg': { width: '1.25rem', height: '1.25rem' },
};

const routingRegionSx: SxProps<Theme> = {
  borderTop: '1px solid',
  borderColor: (theme) => alpha(theme.palette.custom.OV.sys.outlineVariant, 0.5),
  px: 2.25,
  pt: 0.25,
  pb: 2,
};

export const SlaRoutingSection = ({ partnerId }: { partnerId: number | null }) => {
  const {
    policy,
    hasUnsavedChanges,
    isSaving,
    savePolicy,
    discardChanges,
    updateEmailPolicy,
    updateVoicePolicy,
  } = useSlaRoutingPolicy(partnerId);
  const [expanded, setExpanded] = useState(false);
  const assigneeGroupOptions = useConversationAssigneeGroupsOptions(partnerId, {
    skip: !expanded,
  });

  return (
    <Box>
      <ButtonBase
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        sx={{ display: 'block', width: '100%', textAlign: 'left' }}
      >
        <SettingsSectionRow
          icon={<Split />}
          title="SLA-Based Routing"
          secondary="Configure how emails and calls are routed based on client service level."
          controlAlign="start"
          control={
            <Box
              aria-hidden
              sx={[disclosureChevronSx, { transform: expanded ? 'rotate(90deg)' : 'none' }]}
            >
              <ChevronRight />
            </Box>
          }
        />
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={routingRegionSx}>
          <Box>
            <SlaRoutingConfig
              title="Support Email"
              routingMap={policy.email?.support}
              onUpdate={(timeBlock, thresholdMap) =>
                updateEmailPolicy('support', timeBlock, thresholdMap)
              }
              mode="email"
              assigneeGroupOptions={assigneeGroupOptions}
              defaultExpanded
            />
            <SlaRoutingConfig
              title="Membership Email"
              routingMap={policy.email?.membership}
              onUpdate={(timeBlock, thresholdMap) =>
                updateEmailPolicy('membership', timeBlock, thresholdMap)
              }
              mode="email"
              assigneeGroupOptions={assigneeGroupOptions}
              defaultExpanded
            />
            <SlaRoutingConfig
              title="Voice Calls"
              routingMap={policy.voice}
              onUpdate={(timeBlock, thresholdMap) =>
                updateVoicePolicy(timeBlock, thresholdMap)
              }
              mode="voice"
            />
          </Box>

          {hasUnsavedChanges ? (
            <SlaRoutingSaveBar
              isSaving={isSaving}
              onSave={() => void savePolicy()}
              onDiscard={discardChanges}
            />
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
};
